/**
 * SectionVariantPicker — Architonic-style colourway / variant selector.
 *
 * Renders a grid of square IMAGE thumbnails, one per variant (the Architonic
 * / Material Bank pattern). Each tile shows the variant's own image, resolved
 * from the listing's image entities by uuid; when a variant has no image of
 * its own it falls back to a flat colour swatch (the colour_family hex), so
 * colour-only ranges still read cleanly.
 *
 * Selecting a tile swaps the main gallery image (via onVariantChange ->
 * preselectedVariantSku in ListingPageCarousel) and updates the active
 * colourway name + count shown above the grid. Discontinued variants are
 * dimmed but stay selectable/resolvable (resolveVariantForDisplay).
 *
 * Renders nothing for parallel-SKU listings (no publicData.variants) or
 * categories that don't support variants — a no-op for the flat catalogue.
 *
 * Spec: /Users/rogersiew/.claude/plans/right-now-finish-surface-validated-castle.md
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { getVariantAttributesFor } from '../../config/variantAttributes';
import { resolveVariantForDisplay } from '../../util/variantHelpers';
import { COLOUR_HEX } from '../../components/ColorSwatchFilter/colorMap';

import css from './SectionVariantPicker.module.css';

// "Show all" caps — swatches shown before the expander collapses the rest.
// These MUST match the nth-child cut-offs in SectionVariantPicker.module.css
// (the .collapsed rules): 10 below --viewportMedium, 16 at/above it.
const MOBILE_CAP = 10;
const DESKTOP_CAP = 16;

// Smallest available rendered URL for a Sharetribe image entity.
const imageUrl = img => {
  const vs = (img && img.attributes && img.attributes.variants) || {};
  const order = ['scaled-small', 'square-small', 'listing-card', 'scaled-medium', 'default'];
  for (const k of order) if (vs[k] && vs[k].url) return vs[k].url;
  const any = Object.values(vs).find(v => v && v.url);
  return any ? any.url : null;
};

const SectionVariantPicker = props => {
  const { listing, onVariantChange, initialSku, syncSku } = props;
  const publicData = (listing && listing.attributes && listing.attributes.publicData) || {};
  const variants = Array.isArray(publicData.variants) ? publicData.variants : [];
  const categoryLevel2 = publicData.categoryLevel2;
  const axes = useMemo(() => getVariantAttributesFor(categoryLevel2), [categoryLevel2]);

  // uuid -> small image url, from the listing's image entities.
  const urlByImageId = useMemo(() => {
    const map = {};
    for (const img of (listing && listing.images) || []) {
      const uuid = img && img.id && img.id.uuid;
      const url = imageUrl(img);
      if (uuid && url) map[uuid] = url;
    }
    return map;
  }, [listing]);

  const defaultVariant = useMemo(() => {
    if (variants.length === 0) return null;
    if (initialSku) {
      const f = variants.find(v => v.sku === initialSku);
      if (f) return f;
    }
    return variants.find(v => v.available !== false) || variants[0];
  }, [variants, initialSku]);

  const [activeSku, setActiveSku] = useState(defaultVariant ? defaultVariant.sku : null);
  const [expanded, setExpanded] = useState(false);

  // Re-seat the default if it resolves after data load (e.g. ?sku= deep-link).
  useEffect(() => {
    if (defaultVariant && activeSku == null) setActiveSku(defaultVariant.sku);
  }, [defaultVariant, activeSku]);

  // Follow the hero gallery: swiping to another colourway's photo moves the
  // highlight here too (the SKU-equality guard below keeps this loop-free).
  useEffect(() => {
    if (syncSku && syncSku !== activeSku && variants.some(v => v.sku === syncSku)) {
      setActiveSku(syncSku);
    }
  }, [syncSku]);

  const activeVariant = useMemo(() => variants.find(v => v.sku === activeSku) || defaultVariant, [
    variants,
    activeSku,
    defaultVariant,
  ]);

  // Notify parent (gallery / save / board) when the active variant changes.
  // SKU-equality guard prevents a re-render loop (the listing prop rebuilds on
  // unrelated renders, churning the variants array reference).
  const lastReportedSkuRef = useRef(null);
  useEffect(() => {
    if (!activeVariant || typeof onVariantChange !== 'function') return;
    if (lastReportedSkuRef.current === activeVariant.sku) return;
    lastReportedSkuRef.current = activeVariant.sku;
    const d = resolveVariantForDisplay(variants, activeVariant.sku);
    onVariantChange({
      variant: activeVariant,
      sku: activeVariant.sku,
      label: d.label,
      isDiscontinued: d.isDiscontinued,
    });
  }, [activeVariant, variants, onVariantChange]);

  if (variants.length === 0) return null;
  if (axes.length === 0) return null;

  // Tile face: the variant's own image, else a flat colour swatch.
  const tileBg = v => {
    const uuid = Array.isArray(v.images) && v.images[0];
    const url = uuid && urlByImageId[uuid];
    if (url) return { type: 'img', value: url };
    const colour = v.attributes && v.attributes.colour;
    return { type: 'swatch', value: (colour && COLOUR_HEX[colour]) || '#e3e3e3' };
  };

  const variantName = v => resolveVariantForDisplay(variants, v.sku).label || v.sku;

  const activeDisplay = activeVariant
    ? resolveVariantForDisplay(variants, activeVariant.sku)
    : { label: '', isDiscontinued: false };

  return (
    <div className={css.root}>
      <div className={css.header}>
        <span className={css.count}>
          <FormattedMessage
            id="SectionVariantPicker.variations"
            values={{ count: variants.length }}
          />
        </span>
        {activeVariant ? (
          <span
            className={classNames(css.activeName, {
              [css.activeNameDiscontinued]: activeDisplay.isDiscontinued,
            })}
          >
            {activeDisplay.isDiscontinued ? (
              <FormattedMessage
                id="SectionVariantPicker.activeDiscontinued"
                values={{ label: activeDisplay.label }}
              />
            ) : (
              activeDisplay.label
            )}
          </span>
        ) : null}
      </div>

      <div className={classNames(css.grid, { [css.collapsed]: !expanded })}>
        {variants.map(v => {
          const bg = tileBg(v);
          const selected = activeVariant && activeVariant.sku === v.sku;
          const discontinued = v.available === false;
          const name = variantName(v);
          return (
            <button
              key={v.sku}
              type="button"
              aria-pressed={!!selected}
              aria-label={name}
              title={name}
              className={classNames(css.tile, {
                [css.tileSelected]: selected,
                [css.tileDiscontinued]: discontinued,
              })}
              onClick={() => setActiveSku(v.sku)}
            >
              {bg.type === 'img' ? (
                <img src={bg.value} alt={name} className={css.tileImg} loading="lazy" />
              ) : (
                <span className={css.tileSwatch} style={{ backgroundColor: bg.value }} />
              )}
            </button>
          );
        })}
      </div>

      {variants.length > MOBILE_CAP ? (
        <button
          type="button"
          className={classNames(css.showMore, {
            // Below --viewportMedium the CSS caps at 10; at/above it caps at 16.
            // When the count fits the desktop cap, the button is only useful on
            // mobile — hide it on desktop where nothing is collapsed.
            [css.showMoreMobileOnly]: variants.length <= DESKTOP_CAP,
          })}
          onClick={() => setExpanded(x => !x)}
        >
          {expanded ? (
            <FormattedMessage id="SectionVariantPicker.showLess" />
          ) : (
            <FormattedMessage
              id="SectionVariantPicker.showAll"
              values={{ count: variants.length }}
            />
          )}
        </button>
      ) : null}
    </div>
  );
};

export default SectionVariantPicker;
